import {
	FormDrawer,
	FormDrawerContent,
	Id,
	RenderProps,
	isSuccess,
	useBulkEdit,
	usePaginatedList,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { DistributionListSelectField } from "../distributionLists";
import { contactService } from "../shared/api/contactService";
import { distributionListService } from "../shared/api/distributionListService";
import { IDistributionListOption } from "../shared/types/distributionList";

interface FormValues {
	distributionList: IDistributionListOption | null;
}

const initialValues: FormValues = {
	distributionList: null,
};

interface Props extends RenderProps {
	contactFilterId: Id | null;
}

export const DistributionListContactsBulkEditDrawer: FC<Props> = ({ open, handleClose, contactFilterId }) => {
	const { t } = useTranslation();
	const { selectedRows } = useBulkEdit();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const { searchParams } = usePaginatedList();

	const attachContactsToDistributionList = useCallback(
		async ({ distributionList }: FormValues) => {
			if (!distributionList) {
				throw new Error("Distribution list is missing");
			}

			const response = await distributionListService.attachContacts(
				distributionList.id,
				{ contactIds: selectedRows !== "all" ? selectedRows : [] },
				{ search: searchParams?.search, contactFilterId: contactFilterId },
			);

			if (isSuccess(response)) {
				showToast({ type: "success", title: t("Contacts"), body: t("Contacts successfully added") });
				await queryClient.invalidateQueries({ queryKey: distributionListService.showKey(distributionList.id) });
				await queryClient.invalidateQueries({ queryKey: contactService.indexKey });
				return true;
			}

			return false;
		},
		[queryClient, selectedRows, showToast, t, contactFilterId, searchParams?.search],
	);

	const validate = useCallback(
		(values: FormValues) => {
			const errors: Record<string, string> = {};
			if (!values.distributionList) {
				errors.distributionList = t("Required field");
			}
			return errors;
		},
		[t],
	);

	return (
		<FormDrawer
			open={open}
			onSubmit={attachContactsToDistributionList}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<DistributionListSelectField label={t("Distribution list")} name="distributionList" />
				</FormDrawerContent>
			)}
			initialValues={initialValues}
			onClose={handleClose}
			title={t("Add contacts to distribution list")}
			validate={validate}
		/>
	);
};
